import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { OrderingSelectors } from 'polygon-ordering';
import getThemeLookup from '../selectors/getThemeLookup';
import getDeviceTypeMobile from '../selectors/getDeviceTypeMobile';
import getOrderInProgress from '../selectors/getOrderInProgress';
import combineStyles from '../utils/combineStyles';
import { COMPLETED_ORDER_SCREEN_ROUTE, MENU_SCREEN_ROUTE } from '../hooks/useRoutes';
import ScreenHero from '../components/ScreenHero';
import ScreenFloating from '../components/ScreenFloating';
import Text from '../components/Text';
import RedirectAndPreserveSearch from '../components/RedirectAndPreserveSearch';
import { useAppSelector, useAppDispatch } from '../app/hooks';
import SaleTypeListItem from '../components/SaleTypeListItem';
import { OrderingOperations } from 'polygon-ordering';
import { SALE_TYPE_WITH_SUBS } from '../libs/polygon-ordering/src/constants/saleType';
import { IoIosArrowBack } from 'react-icons/io';
import TouchableOpacity from '../components/TouchableOpacity';
import history from '../history';

const { getOrderSubmitted, getBypassDeliveryDetails, getSubDisplayableSaleTypes } =
  OrderingSelectors;
const { setBypassDeliveryDetails } = OrderingOperations;

const PickSaleTypeChild = () => {
  const p = useAppSelector(getThemeLookup);
  const deviceTypeMobile = useAppSelector(getDeviceTypeMobile);
  const orderInProgress = useAppSelector(getOrderInProgress);
  const orderSubmitted = useAppSelector(getOrderSubmitted);
  const dispatch = useAppDispatch();

  const { t } = useTranslation();
  const pathname = useLocation();
  const bypassDeliveryDetails = useAppSelector(getBypassDeliveryDetails);

  const getLocationId = () => {
    let locationId = null;

    if (pathname.state) {
      locationId = pathname.state.locationId;
    }

    return locationId;
  };

  const browserLocation = useLocation<Location>();
  const activeSaleType = browserLocation?.state?.saleType as SALE_TYPE_WITH_SUBS;
  const saleTypeTitle = t(`saleType.${[activeSaleType]}.name`);

  const displayOptions = useAppSelector(getSubDisplayableSaleTypes(activeSaleType));

  const ScreenComponent = deviceTypeMobile ? ScreenHero : ScreenFloating;
  const columnStyles = combineStyles(styles.column, deviceTypeMobile && styles.columnMobile);

  if (orderSubmitted) {
    return <RedirectAndPreserveSearch to={COMPLETED_ORDER_SCREEN_ROUTE} />;
  }

  if (orderInProgress) {
    return <RedirectAndPreserveSearch to={MENU_SCREEN_ROUTE} />;
  } else {
    if (bypassDeliveryDetails) {
      dispatch(setBypassDeliveryDetails(false));
    }
  }

  return (
    <ScreenComponent>
      <div
        style={combineStyles(
          deviceTypeMobile ? styles.mainContainerMobile : styles.mainContainerDesktop,
          p('screen', ['backgroundColor']),
          p(deviceTypeMobile ? 'initialScreenMobile' : 'initialScreenDesktop', [
            'backgroundColor',
            'boxShadow',
          ]),
        )}
      >
        <TouchableOpacity
          style={styles.backButton}
          onClick={() => {
            history.goBack();
          }}
        >
          <IoIosArrowBack style={{ fontSize: 35 }} />
        </TouchableOpacity>

        <div style={columnStyles}>
          {!deviceTypeMobile && (
            <div className="text-center mb-3">
              <Text variant="h1" value={saleTypeTitle} />
            </div>
          )}

          {displayOptions?.map(saleType => (
            <SaleTypeListItem saleType={saleType} locationId={getLocationId()} key={saleType} />
          ))}
        </div>
      </div>
    </ScreenComponent>
  );
};

const styles: Styles = {
  mainContainerDesktop: {
    borderRadius: 5,
    padding: 30,
    paddingTop: 50,
    paddingBottom: 50,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'stretch',
    margin: 'auto',
    maxWidth: 550,
    width: '100%',
    justifyContent: 'center',
    marginTop: '5vh',
    minHeight: 'calc(100% - 5vh * 2)',
    position: 'relative',
  },
  mainContainerMobile: {
    flex: 1,
    padding: '10px 25px 100px 25px',
  },
  column: {
    maxWidth: 340,
    minHeight: 500,
    gap: 15,
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  columnMobile: {
    maxWidth: 'auto',
    padding: '20px 0 0 0',
    height: '100%',
  },
  backButton: {
    position: 'absolute',
    top: 48,
    alignSelf: 'flex-start',
    left: 24,
  },
};

export default PickSaleTypeChild;
